import { useMemo, useState } from "react";
import useAdminQuery from "../../lib/queries/useAdminQuery";
import usePagedAdminQuery from "../../lib/queries/usePagedAdminQuery";
import { Topbar } from "../../components/AppShell";
import { KpiCard, EmptyState } from "../../lib/funnel/charts";
import { fmtN, fmtPct } from "../../lib/funnel/helpers";
import { GOLD, FG3 } from "../../lib/funnel/theme";
import { QueryInspector } from "../../lib/funnel/QueryInspector";
import type { FilterPillSpec } from "../../components/FilterPills";
import {
  BASELINE_SQL, ALL_USERS_SQL, DEMO_SQL,
  SUPPORT_NO_DEMO_SQL, SUPPORT_DEMO_SQL,
  STRIPE_FIRST_PAID_INVOICE_SQL,
  COMPANY_INSIGHTS_BASELINE_SQL, COMPANY_INSIGHTS_ALL_USERS_SQL,
  COMPANY_INSIGHTS_DEMO_SQL, COMPANY_INSIGHTS_SUPPORT_NO_DEMO_SQL,
  COMPANY_INSIGHTS_SUPPORT_DEMO_SQL,
  mapRows, mapStripeFirstPaid, enrichPaidConvertedDate,
  mapCompanyInsights,
} from "./sql";
import type { CompanyInsightsRow } from "./sql";
import type { BaselineRow, CohortMode } from "./sql";
import {
  dedupeByUser,
  twoSummariesWithin14d,
  calendarConnected,
  calendarWithin7d,
  plusWithin30d,
  medianDaysToFirstSummary,
  mdeAtBaseline,
} from "./helpers";
import { FiltersBar, applyFilters, defaultFilters, defaultComparisonFilters } from "./filters";
import type { Filters } from "./filters";
import { describeTiming } from "./stages";
import { BaselineFunnel } from "./BaselineFunnel";
import { FeatureWeekHeatmap } from "./FeatureWeekHeatmap";
import { FeatureConversionTable } from "./FeatureConversionTable";
import { ActivityHistogram } from "./ActivityHistogram";
import { RetentionCurve } from "./RetentionCurve";
import { FirstEventHistogram } from "./FirstEventHistogram";
import { CompanyInsights } from "./CompanyInsights";
import {
  ACTIVITY_RAW_24H_SQL, ACTIVITY_DAILY_90D_SQL,
  ALL_USERS_ACTIVITY_RAW_24H_SQL, ALL_USERS_ACTIVITY_DAILY_90D_SQL,
  DEMO_ACTIVITY_RAW_24H_SQL, DEMO_ACTIVITY_DAILY_90D_SQL,
  SUPPORT_NO_DEMO_ACTIVITY_RAW_24H_SQL, SUPPORT_NO_DEMO_ACTIVITY_DAILY_90D_SQL,
  SUPPORT_DEMO_ACTIVITY_RAW_24H_SQL, SUPPORT_DEMO_ACTIVITY_DAILY_90D_SQL,
  mapRaw24h, mapDaily,
} from "./activitySql";
import type { ActivityRaw24h, ActivityDaily } from "./activitySql";
import { OnboardingBaselineExplain, OnboardingBaselineSchema } from "./explainBlocks";

export function OnboardingBaseline() {
  const [filters, setFilters] = useState<Filters>(() => defaultFilters());
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [compareFilters, setCompareFilters] = useState<Filters>(() => defaultComparisonFilters());
  const [inspectorOpen, setInspectorOpen] = useState(false);

  const need = (m: CohortMode) =>
    filters.cohortMode === m || (compareEnabled && compareFilters.cohortMode === m);
  const needsBaseline = need("baseline");
  const needsAllUsers = need("all_users");
  const needsDemo = need("demo");
  const needsSupportNoDemo = need("support_no_demo");
  const needsSupportDemo = need("support_demo");

  const baselineResult = useAdminQuery(BASELINE_SQL, [], { enabled: needsBaseline });
  const allUsersResult = useAdminQuery(ALL_USERS_SQL, [], { enabled: needsAllUsers });
  const demoResult = useAdminQuery(DEMO_SQL, [], { enabled: needsDemo });
  const supportNoDemoResult = useAdminQuery(SUPPORT_NO_DEMO_SQL, [], { enabled: needsSupportNoDemo });
  const supportDemoResult = useAdminQuery(SUPPORT_DEMO_SQL, [], { enabled: needsSupportDemo });
  const stripeFirstPaidResult = useAdminQuery(STRIPE_FIRST_PAID_INVOICE_SQL, [], { database: "stripe" });

  const companyBaselineResult = useAdminQuery(COMPANY_INSIGHTS_BASELINE_SQL, [], { enabled: needsBaseline });
  const companyAllUsersResult = useAdminQuery(COMPANY_INSIGHTS_ALL_USERS_SQL, [], { enabled: needsAllUsers });
  const companyDemoResult = useAdminQuery(COMPANY_INSIGHTS_DEMO_SQL, [], { enabled: needsDemo });
  const companySupportNoDemoResult = useAdminQuery(COMPANY_INSIGHTS_SUPPORT_NO_DEMO_SQL, [], { enabled: needsSupportNoDemo });
  const companySupportDemoResult = useAdminQuery(COMPANY_INSIGHTS_SUPPORT_DEMO_SQL, [], { enabled: needsSupportDemo });

  const companyBaselineRows = useMemo<CompanyInsightsRow[]>(() => {
    if (!companyBaselineResult.data) return [];
    return mapCompanyInsights(companyBaselineResult.data.columns, companyBaselineResult.data.rows as unknown[][]);
  }, [companyBaselineResult.data]);
  const companyAllUsersRows = useMemo<CompanyInsightsRow[]>(() => {
    if (!companyAllUsersResult.data) return [];
    return mapCompanyInsights(companyAllUsersResult.data.columns, companyAllUsersResult.data.rows as unknown[][]);
  }, [companyAllUsersResult.data]);
  const companyDemoRows = useMemo<CompanyInsightsRow[]>(() => {
    if (!companyDemoResult.data) return [];
    return mapCompanyInsights(companyDemoResult.data.columns, companyDemoResult.data.rows as unknown[][]);
  }, [companyDemoResult.data]);
  const companySupportNoDemoRows = useMemo<CompanyInsightsRow[]>(() => {
    if (!companySupportNoDemoResult.data) return [];
    return mapCompanyInsights(companySupportNoDemoResult.data.columns, companySupportNoDemoResult.data.rows as unknown[][]);
  }, [companySupportNoDemoResult.data]);
  const companySupportDemoRows = useMemo<CompanyInsightsRow[]>(() => {
    if (!companySupportDemoResult.data) return [];
    return mapCompanyInsights(companySupportDemoResult.data.columns, companySupportDemoResult.data.rows as unknown[][]);
  }, [companySupportDemoResult.data]);

  const stripeFirstPaidByCustomer = useMemo(() => {
    if (!stripeFirstPaidResult.data) return new Map<string, string>();
    return mapStripeFirstPaid(stripeFirstPaidResult.data.columns, stripeFirstPaidResult.data.rows as unknown[][]);
  }, [stripeFirstPaidResult.data]);

  const baselineRows = useMemo<BaselineRow[]>(() => {
    if (!baselineResult.data) return [];
    const rows = mapRows(baselineResult.data.columns, baselineResult.data.rows as unknown[][]);
    return enrichPaidConvertedDate(rows, stripeFirstPaidByCustomer);
  }, [baselineResult.data, stripeFirstPaidByCustomer]);
  const allUsersRows = useMemo<BaselineRow[]>(() => {
    if (!allUsersResult.data) return [];
    const rows = mapRows(allUsersResult.data.columns, allUsersResult.data.rows as unknown[][]);
    return enrichPaidConvertedDate(rows, stripeFirstPaidByCustomer);
  }, [allUsersResult.data, stripeFirstPaidByCustomer]);
  const demoRows = useMemo<BaselineRow[]>(() => {
    if (!demoResult.data) return [];
    const rows = mapRows(demoResult.data.columns, demoResult.data.rows as unknown[][]);
    return enrichPaidConvertedDate(rows, stripeFirstPaidByCustomer);
  }, [demoResult.data, stripeFirstPaidByCustomer]);
  const supportNoDemoRows = useMemo<BaselineRow[]>(() => {
    if (!supportNoDemoResult.data) return [];
    const rows = mapRows(supportNoDemoResult.data.columns, supportNoDemoResult.data.rows as unknown[][]);
    return enrichPaidConvertedDate(rows, stripeFirstPaidByCustomer);
  }, [supportNoDemoResult.data, stripeFirstPaidByCustomer]);
  const supportDemoRows = useMemo<BaselineRow[]>(() => {
    if (!supportDemoResult.data) return [];
    const rows = mapRows(supportDemoResult.data.columns, supportDemoResult.data.rows as unknown[][]);
    return enrichPaidConvertedDate(rows, stripeFirstPaidByCustomer);
  }, [supportDemoResult.data, stripeFirstPaidByCustomer]);

  const baselineAll = useMemo(() => dedupeByUser(baselineRows), [baselineRows]);
  const allUsersAll = useMemo(() => dedupeByUser(allUsersRows), [allUsersRows]);
  const demoAll = useMemo(() => dedupeByUser(demoRows), [demoRows]);
  const supportNoDemoAll = useMemo(() => dedupeByUser(supportNoDemoRows), [supportNoDemoRows]);
  const supportDemoAll = useMemo(() => dedupeByUser(supportDemoRows), [supportDemoRows]);

  const sourceFor = (f: Filters) =>
    f.cohortMode === "baseline"        ? baselineAll
    : f.cohortMode === "demo"          ? demoAll
    : f.cohortMode === "support_no_demo" ? supportNoDemoAll
    : f.cohortMode === "support_demo"  ? supportDemoAll
    : allUsersAll;
  const users = useMemo(
    () => applyFilters(sourceFor(filters), filters),
    [filters, baselineAll, allUsersAll, demoAll, supportNoDemoAll, supportDemoAll],
  );
  const compareUsers = useMemo(
    () => compareEnabled ? applyFilters(sourceFor(compareFilters), compareFilters) : null,
    [compareEnabled, compareFilters, baselineAll, allUsersAll, demoAll, supportNoDemoAll, supportDemoAll],
  );

  const companySourceFor = (f: Filters) =>
    f.cohortMode === "baseline"          ? companyBaselineRows
    : f.cohortMode === "demo"            ? companyDemoRows
    : f.cohortMode === "support_no_demo" ? companySupportNoDemoRows
    : f.cohortMode === "support_demo"    ? companySupportDemoRows
    : companyAllUsersRows;
  const scopedCompanies = useMemo(() => {
    const scope = new Set(users.map((u) => u.company_external_id ?? ""));
    return companySourceFor(filters).filter((c) => scope.has(c.company_external_id));
  }, [users, filters, companyBaselineRows, companyAllUsersRows, companyDemoRows, companySupportNoDemoRows, companySupportDemoRows]);
  const scopedCompareCompanies = useMemo(() => {
    if (!compareUsers) return null;
    const scope = new Set(compareUsers.map((u) => u.company_external_id ?? ""));
    return companySourceFor(compareFilters).filter((c) => scope.has(c.company_external_id));
  }, [compareUsers, compareFilters, companyBaselineRows, companyAllUsersRows, companyDemoRows, companySupportNoDemoRows, companySupportDemoRows]);

  const raw24hBaselineResult = useAdminQuery(ACTIVITY_RAW_24H_SQL, [], { enabled: needsBaseline });
  const dailyBaselineResult = usePagedAdminQuery(ACTIVITY_DAILY_90D_SQL, [], { enabled: needsBaseline });
  const raw24hAllUsersResult = useAdminQuery(ALL_USERS_ACTIVITY_RAW_24H_SQL, [], { enabled: needsAllUsers });
  const dailyAllUsersResult = usePagedAdminQuery(ALL_USERS_ACTIVITY_DAILY_90D_SQL, [], { enabled: needsAllUsers });
  const raw24hDemoResult = useAdminQuery(DEMO_ACTIVITY_RAW_24H_SQL, [], { enabled: needsDemo });
  const dailyDemoResult = usePagedAdminQuery(DEMO_ACTIVITY_DAILY_90D_SQL, [], { enabled: needsDemo });
  const raw24hSupportNoDemoResult = useAdminQuery(SUPPORT_NO_DEMO_ACTIVITY_RAW_24H_SQL, [], { enabled: needsSupportNoDemo });
  const dailySupportNoDemoResult = usePagedAdminQuery(SUPPORT_NO_DEMO_ACTIVITY_DAILY_90D_SQL, [], { enabled: needsSupportNoDemo });
  const raw24hSupportDemoResult = useAdminQuery(SUPPORT_DEMO_ACTIVITY_RAW_24H_SQL, [], { enabled: needsSupportDemo });
  const dailySupportDemoResult = usePagedAdminQuery(SUPPORT_DEMO_ACTIVITY_DAILY_90D_SQL, [], { enabled: needsSupportDemo });

  const raw24hBaseline = useMemo<ActivityRaw24h[]>(() => {
    if (!raw24hBaselineResult.data) return [];
    return mapRaw24h(raw24hBaselineResult.data.columns, raw24hBaselineResult.data.rows as unknown[][]);
  }, [raw24hBaselineResult.data]);
  const dailyBaseline = useMemo<ActivityDaily[]>(() => {
    if (!dailyBaselineResult.data) return [];
    return mapDaily(dailyBaselineResult.data.columns, dailyBaselineResult.data.rows as unknown[][]);
  }, [dailyBaselineResult.data]);
  const raw24hAllUsers = useMemo<ActivityRaw24h[]>(() => {
    if (!raw24hAllUsersResult.data) return [];
    return mapRaw24h(raw24hAllUsersResult.data.columns, raw24hAllUsersResult.data.rows as unknown[][]);
  }, [raw24hAllUsersResult.data]);
  const dailyAllUsers = useMemo<ActivityDaily[]>(() => {
    if (!dailyAllUsersResult.data) return [];
    return mapDaily(dailyAllUsersResult.data.columns, dailyAllUsersResult.data.rows as unknown[][]);
  }, [dailyAllUsersResult.data]);
  const raw24hDemo = useMemo<ActivityRaw24h[]>(() => {
    if (!raw24hDemoResult.data) return [];
    return mapRaw24h(raw24hDemoResult.data.columns, raw24hDemoResult.data.rows as unknown[][]);
  }, [raw24hDemoResult.data]);
  const dailyDemo = useMemo<ActivityDaily[]>(() => {
    if (!dailyDemoResult.data) return [];
    return mapDaily(dailyDemoResult.data.columns, dailyDemoResult.data.rows as unknown[][]);
  }, [dailyDemoResult.data]);
  const raw24hSupportNoDemo = useMemo<ActivityRaw24h[]>(() => {
    if (!raw24hSupportNoDemoResult.data) return [];
    return mapRaw24h(raw24hSupportNoDemoResult.data.columns, raw24hSupportNoDemoResult.data.rows as unknown[][]);
  }, [raw24hSupportNoDemoResult.data]);
  const dailySupportNoDemo = useMemo<ActivityDaily[]>(() => {
    if (!dailySupportNoDemoResult.data) return [];
    return mapDaily(dailySupportNoDemoResult.data.columns, dailySupportNoDemoResult.data.rows as unknown[][]);
  }, [dailySupportNoDemoResult.data]);
  const raw24hSupportDemo = useMemo<ActivityRaw24h[]>(() => {
    if (!raw24hSupportDemoResult.data) return [];
    return mapRaw24h(raw24hSupportDemoResult.data.columns, raw24hSupportDemoResult.data.rows as unknown[][]);
  }, [raw24hSupportDemoResult.data]);
  const dailySupportDemo = useMemo<ActivityDaily[]>(() => {
    if (!dailySupportDemoResult.data) return [];
    return mapDaily(dailySupportDemoResult.data.columns, dailySupportDemoResult.data.rows as unknown[][]);
  }, [dailySupportDemoResult.data]);

  const truncatedSources = useMemo(() => {
    const list: string[] = [];
    const check = (label: string, data: { truncated?: boolean; rowCount?: number } | undefined) => {
      if (data?.truncated) list.push(`${label} (${data.rowCount ?? "?"} rows returned)`);
    };
    if (needsBaseline) {
      check("Baseline cohort", baselineResult.data);
      check("Baseline activity (24h)", raw24hBaselineResult.data);
      check("Baseline activity (90d daily)", dailyBaselineResult.data);
    }
    if (needsAllUsers) {
      check("All users cohort", allUsersResult.data);
      check("All users activity (24h)", raw24hAllUsersResult.data);
      check("All users activity (90d daily)", dailyAllUsersResult.data);
    }
    if (needsDemo) {
      check("Demo cohort", demoResult.data);
      check("Demo activity (24h)", raw24hDemoResult.data);
      check("Demo activity (90d daily)", dailyDemoResult.data);
    }
    if (needsSupportNoDemo) {
      check("Support (no demo) cohort", supportNoDemoResult.data);
      check("Support (no demo) activity (24h)", raw24hSupportNoDemoResult.data);
      check("Support (no demo) activity (90d daily)", dailySupportNoDemoResult.data);
    }
    if (needsSupportDemo) {
      check("Support (demo'd) cohort", supportDemoResult.data);
      check("Support (demo'd) activity (24h)", raw24hSupportDemoResult.data);
      check("Support (demo'd) activity (90d daily)", dailySupportDemoResult.data);
    }
    return list;
  }, [
    needsBaseline, needsAllUsers, needsDemo, needsSupportNoDemo, needsSupportDemo,
    baselineResult.data, allUsersResult.data, demoResult.data,
    supportNoDemoResult.data, supportDemoResult.data,
    raw24hBaselineResult.data, dailyBaselineResult.data,
    raw24hAllUsersResult.data, dailyAllUsersResult.data,
    raw24hDemoResult.data, dailyDemoResult.data,
    raw24hSupportNoDemoResult.data, dailySupportNoDemoResult.data,
    raw24hSupportDemoResult.data, dailySupportDemoResult.data,
  ]);

  const sliceActivity = (f: Filters, usersSlice: BaselineRow[]) => {
    const r =
      f.cohortMode === "baseline"        ? raw24hBaseline
      : f.cohortMode === "demo"          ? raw24hDemo
      : f.cohortMode === "support_no_demo" ? raw24hSupportNoDemo
      : f.cohortMode === "support_demo"  ? raw24hSupportDemo
      : raw24hAllUsers;
    const d =
      f.cohortMode === "baseline"        ? dailyBaseline
      : f.cohortMode === "demo"          ? dailyDemo
      : f.cohortMode === "support_no_demo" ? dailySupportNoDemo
      : f.cohortMode === "support_demo"  ? dailySupportDemo
      : dailyAllUsers;
    const ids = new Set(usersSlice.map((u) => u.user_external_id));
    return {
      raw24h: r.filter((x) => ids.has(x.user_external_id)),
      daily: d.filter((x) => ids.has(x.user_external_id)),
    };
  };

  const baselineActivity = useMemo(() => sliceActivity(filters, users),
    [
      filters, users,
      raw24hBaseline, dailyBaseline, raw24hAllUsers, dailyAllUsers,
      raw24hDemo, dailyDemo,
      raw24hSupportNoDemo, dailySupportNoDemo,
      raw24hSupportDemo, dailySupportDemo,
    ]);
  const compareActivity = useMemo(() => {
    if (!compareEnabled || !compareUsers) return null;
    return sliceActivity(compareFilters, compareUsers);
  }, [
    compareEnabled, compareFilters, compareUsers,
    raw24hBaseline, dailyBaseline, raw24hAllUsers, dailyAllUsers,
    raw24hDemo, dailyDemo,
    raw24hSupportNoDemo, dailySupportNoDemo,
    raw24hSupportDemo, dailySupportDemo,
  ]);

  const computeKpis = (rows: BaselineRow[]) => {
    const n = rows.length;
    const twoSummaries = rows.filter(twoSummariesWithin14d).length;
    const calendar7d = rows.filter(calendarWithin7d).length;
    const calendarLifetime = rows.filter(calendarConnected).length;
    const medianTts = medianDaysToFirstSummary(rows);
    const transcripts14d = rows.reduce((acc, u) => acc + u.transcripts_14d_meeting_count, 0);
    const synopsised14d = rows.reduce((acc, u) => acc + u.synopsised_14d_meeting_count, 0);
    const plus30d = rows.filter(plusWithin30d).length;
    return { n, twoSummaries, calendar7d, calendarLifetime, medianTts, transcripts14d, synopsised14d, plus30d };
  };
  const kpis = useMemo(() => computeKpis(users), [users]);
  const compareKpis = useMemo(() => compareUsers ? computeKpis(compareUsers) : null, [compareUsers]);

  const pillsFor = (f: Filters): FilterPillSpec[] => {
    const pills: FilterPillSpec[] = [];
    const cohortLabel: Record<CohortMode, string> = {
      baseline: "Baseline cohort",
      demo: "Demo'd companies",
      support_no_demo: "Support role, no demo",
      support_demo: "Support role, demo'd",
      all_users: "All users",
    };
    pills.push({ kind: "mode", label: cohortLabel[f.cohortMode] });
    pills.push({ kind: "range", label: `Last ${f.lookbackDays}d` });
    if (f.months.length > 0) pills.push({ kind: "cohort", label: f.months.length === 1 ? (f.months[0] ?? "") : `${f.months.length} months` });
    if (f.signupSource !== "all") pills.push({ kind: "mode", label: f.signupSource === "first_company" ? "First company sign-up" : "Team invite" });
    if (f.regions.length > 0) pills.push({ kind: "country", label: f.regions.join(" · ") });
    if (f.companySizes.length > 0) pills.push({ kind: "size", label: `Size: ${f.companySizes.join(" · ")}` });
    for (const t of f.stageTimings) pills.push({ kind: "mode", label: describeTiming(t) });
    return pills;
  };
  const pagePills = useMemo(() => pillsFor(filters), [filters]);
  const comparePills = useMemo(() => compareEnabled ? pillsFor(compareFilters) : undefined, [compareEnabled, compareFilters]);

  const resultFor = (mode: Filters["cohortMode"]) =>
    mode === "baseline"        ? baselineResult
    : mode === "demo"          ? demoResult
    : mode === "support_no_demo" ? supportNoDemoResult
    : mode === "support_demo"  ? supportDemoResult
    : allUsersResult;
  const activeResult = resultFor(filters.cohortMode);
  const isLoading = activeResult.isLoading || (compareEnabled && resultFor(compareFilters.cohortMode).isLoading);
  const error = activeResult.error ?? (compareEnabled ? resultFor(compareFilters.cohortMode).error : null);

  const pp = (count: number, n: number) => n === 0 ? 0 : (count / n) * 100;
  const compareDelta = (b: { num: number; n: number }, c: { num: number; n: number } | null) => {
    if (!c || c.n === 0) return null;
    return pp(c.num, c.n) - pp(b.num, b.n);
  };

  return (
    <>
      <Topbar crumbs={["Dashboards", "Onboarding Baseline"]} />
      <div className="page">
        <div className="page-header">
          <div>
            <div className="eyebrow">Product · onboarding</div>
            <h1 style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              Onboarding baseline cohort
              <button
                type="button"
                onClick={() => setInspectorOpen(true)}
                title="View SQL, schema and raw rows"
                aria-label="Open query inspector"
                style={{
                  background: "transparent",
                  border: "1px solid var(--border-default)",
                  borderRadius: 6,
                  cursor: "pointer",
                  color: "var(--fg-2)",
                  width: 28,
                  height: 28,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i className="ti ti-info-circle" style={{ fontSize: 16 }} />
              </button>
            </h1>
            <p className="lede">
              Pick a cohort definition and slice by sign-up month, source, region, and company size. Enable comparison
              to overlay a second cohort in gold for an A/B read.
            </p>
            <div className="lede" style={{ fontSize: 12, color: "var(--fg-3)", marginTop: 6 }}>
              <strong style={{ color: "var(--fg-2)" }}>Agreed baseline cohort:</strong> active users who signed up in the
              trailing 360-day window ending 30 days ago, with <em>none</em> of: support role, @gomarloo.com email,
              instant paid conversion (first paid sub started within 1h of sign-up), or any active non-consumer-email
              co-worker who has attended a Marloo-organised demo. One row per (user, company) pair, deduped.
              <br />
              <strong style={{ color: "var(--fg-2)" }}>Demo'd companies:</strong> same window and same exclusions
              (support, @gomarloo, instant-paid) but flipped on the demo check; the user's company must have at least
              one active non-consumer-email member who attended a Marloo-organised demo.
              <br />
              <strong style={{ color: "var(--fg-2)" }}>All users:</strong> every active user in the lookback window, no
              exclusions (window runs all the way to now).
              <br />
              <strong style={{ color: "var(--fg-2)" }}>Support role, no demo:</strong> same window, @gomarloo and demo
              exclusions as baseline, but role = support and the instant-paid filter is dropped (so team-joiners on an
              already-paid plan are kept).
              <br />
              <strong style={{ color: "var(--fg-2)" }}>Support role, demo'd:</strong> same as Support no demo but flipped
              on the demo check; the company must have a non-consumer-email member who attended a Marloo demo. Mutually
              exclusive with the previous cohort on the demo dimension.
            </div>
          </div>
        </div>

        <FiltersBar
          filters={filters}
          onChange={setFilters}
          cohortSize={users.length}
          tone="baseline"
          headerLabel="Baseline"
          headerExtra={
            !compareEnabled && (
              <button
                type="button"
                onClick={() => setCompareEnabled(true)}
                className="btn btn-sm"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  borderColor: GOLD, color: GOLD, fontWeight: 600,
                }}
              >
                <i className="ti ti-git-compare" style={{ fontSize: 13 }} />
                Add comparison cohort
              </button>
            )
          }
        />
        {compareEnabled && (
          <FiltersBar
            filters={compareFilters}
            onChange={setCompareFilters}
            cohortSize={compareUsers?.length ?? 0}
            tone="comparison"
            headerLabel="Comparison"
            headerExtra={
              <button
                type="button"
                onClick={() => setCompareEnabled(false)}
                className="btn btn-sm"
                style={{ display: "inline-flex", alignItems: "center", gap: 4, color: FG3 }}
                title="Disable comparison"
              >
                <i className="ti ti-x" style={{ fontSize: 13 }} />
                Remove
              </button>
            }
          />
        )}

        {truncatedSources.length > 0 && (
          <div
            role="alert"
            style={{
              margin: "12px 0",
              padding: "10px 14px",
              border: "1px solid var(--border-default)",
              borderLeft: "4px solid #d97706",
              borderRadius: 6,
              background: "rgba(217,119,6,0.06)",
              fontSize: 12,
              color: "var(--fg-1)",
              lineHeight: 1.5,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 600, marginBottom: 4 }}>
              <i className="ti ti-alert-triangle" style={{ fontSize: 14, color: "#d97706" }} />
              Query response truncated — figures below are under-counted
            </div>
            <div style={{ color: "var(--fg-2)" }}>
              The /admin/query endpoint capped the row count for:&nbsp;
              {truncatedSources.map((s, i) => (
                <span key={s}>
                  {i > 0 ? ", " : ""}<strong>{s}</strong>
                </span>
              ))}
              . Retention curve and activity histogram only see users included in the truncated payload. Raise the row
              limit on the endpoint, or aggregate the activity SQL more coarsely, to restore accuracy.
            </div>
          </div>
        )}

        {isLoading ? (
          <p style={{ color: "var(--fg-3)" }}>Loading…</p>
        ) : error ? (
          <p style={{ color: "var(--fg-danger)" }}>{(error as Error).message}</p>
        ) : users.length === 0 ? (
          <EmptyState message="No users match the current filters." />
        ) : (
          <>
            <div className="kpi-grid" style={{ marginBottom: 20, gridTemplateColumns: "repeat(5, 1fr)" }}>
              <KpiCard
                label="14d activation rate"
                value={fmtPct(kpis.twoSummaries, kpis.n)}
                sub={`${fmtN(kpis.twoSummaries)} / ${fmtN(kpis.n)} users`}
                tone="primary"
                info={`Share of cohort with 2+ non-demo summaries within 14 days of sign-up (second summary date must fall inside the window). N=${fmtN(kpis.n)}; MDE at this N ~${mdeAtBaseline(kpis.n).toFixed(1)}pp vs an equal-sized comparison.`}
                compareValue={compareKpis ? fmtPct(compareKpis.twoSummaries, compareKpis.n) : undefined}
                compareSub={compareKpis ? `${fmtN(compareKpis.twoSummaries)} / ${fmtN(compareKpis.n)}` : undefined}
                compareDeltaPp={compareDelta({ num: kpis.twoSummaries, n: kpis.n }, compareKpis ? { num: compareKpis.twoSummaries, n: compareKpis.n } : null)}
                compareColor={GOLD}
              />
              <KpiCard
                label="Calendar connected in 7d"
                value={fmtPct(kpis.calendar7d, kpis.n)}
                sub={`${fmtN(kpis.calendar7d)} / ${fmtN(kpis.n)} users · lifetime ${fmtPct(kpis.calendarLifetime, kpis.n)} (${fmtN(kpis.calendarLifetime)})`}
                info={`Share of cohort with a calendar source connected within 7 days of sign-up. Lifetime connection rate (any time after sign-up) is ${fmtPct(kpis.calendarLifetime, kpis.n)} (${fmtN(kpis.calendarLifetime)} / ${fmtN(kpis.n)}).`}
                compareValue={compareKpis ? fmtPct(compareKpis.calendar7d, compareKpis.n) : undefined}
                compareSub={compareKpis ? `${fmtN(compareKpis.calendar7d)} / ${fmtN(compareKpis.n)} · lifetime ${fmtPct(compareKpis.calendarLifetime, compareKpis.n)}` : undefined}
                compareDeltaPp={compareDelta({ num: kpis.calendar7d, n: kpis.n }, compareKpis ? { num: compareKpis.calendar7d, n: compareKpis.n } : null)}
                compareColor={GOLD}
              />
              <KpiCard
                label="Time to first summary"
                value={kpis.medianTts == null ? "—" : `${kpis.medianTts.toFixed(1)} d`}
                sub={`median, ${fmtN(users.filter((u) => u.first_synopsis_date != null).length)} of ${fmtN(kpis.n)} ever summarised`}
                info="Median days from sign-up to the user's first non-demo summary. Users who never summarised are excluded — the KPI answers 'how long does it take given they do it'."
                compareValue={compareKpis ? (compareKpis.medianTts == null ? "—" : `${compareKpis.medianTts.toFixed(1)} d`) : undefined}
                compareSub={compareKpis && compareUsers ? `median, ${fmtN(compareUsers.filter((u) => u.first_synopsis_date != null).length)} of ${fmtN(compareKpis.n)}` : undefined}
                compareColor={GOLD}
              />
              <KpiCard
                label="Transcript -> summary (14d)"
                value={fmtPct(kpis.synopsised14d, kpis.transcripts14d)}
                sub={`${fmtN(kpis.synopsised14d)} / ${fmtN(kpis.transcripts14d)} meetings`}
                info="Of distinct meetings with a transcript in the user's first 14 days, the share that also had at least one summary by the same user (any time). Deduped to one row per (user, meeting), so multiple summaries on the same meeting count once."
                compareValue={compareKpis ? fmtPct(compareKpis.synopsised14d, compareKpis.transcripts14d) : undefined}
                compareSub={compareKpis ? `${fmtN(compareKpis.synopsised14d)} / ${fmtN(compareKpis.transcripts14d)}` : undefined}
                compareDeltaPp={compareDelta({ num: kpis.synopsised14d, n: kpis.transcripts14d }, compareKpis ? { num: compareKpis.synopsised14d, n: compareKpis.transcripts14d } : null)}
                compareColor={GOLD}
              />
              <KpiCard
                label="Free -> Plus in 30d"
                value={fmtPct(kpis.plus30d, kpis.n)}
                sub={`${fmtN(kpis.plus30d)} / ${fmtN(kpis.n)} users`}
                info="Share of cohort whose company started a Plus subscription within 30 days of the user's sign-up. Pro-only conversions are not counted; pre-existing Plus subs (before sign-up) are excluded by the within-window check."
                compareValue={compareKpis ? fmtPct(compareKpis.plus30d, compareKpis.n) : undefined}
                compareSub={compareKpis ? `${fmtN(compareKpis.plus30d)} / ${fmtN(compareKpis.n)}` : undefined}
                compareDeltaPp={compareDelta({ num: kpis.plus30d, n: kpis.n }, compareKpis ? { num: compareKpis.plus30d, n: compareKpis.n } : null)}
                compareColor={GOLD}
              />
            </div>

            <div style={{ display: "grid", gap: 16 }}>
              <BaselineFunnel
                users={users}
                pagePills={pagePills}
                compareUsers={compareUsers}
                comparePills={comparePills}
              />
              <FeatureWeekHeatmap
                users={users}
                pagePills={pagePills}
                compareUsers={compareUsers}
                comparePills={comparePills}
              />
              <FeatureConversionTable
                users={users}
                pagePills={pagePills}
                compareUsers={compareUsers}
                comparePills={comparePills}
              />
              <FirstEventHistogram
                users={users}
                pagePills={pagePills}
                compareUsers={compareUsers}
                comparePills={comparePills}
              />
              <ActivityHistogram
                pagePills={pagePills}
                raw24h={baselineActivity.raw24h}
                daily={baselineActivity.daily}
                cohortSize={users.length}
                loading={raw24hBaselineResult.isLoading || dailyBaselineResult.isLoading}
                compareRaw24h={compareActivity?.raw24h ?? null}
                compareDaily={compareActivity?.daily ?? null}
                compareCohortSize={compareUsers?.length ?? null}
                comparePills={comparePills}
              />
              <RetentionCurve
                pagePills={pagePills}
                users={users}
                daily={baselineActivity.daily}
                cohortSize={users.length}
                loading={dailyBaselineResult.isLoading}
                compareUsers={compareUsers}
                compareDaily={compareActivity?.daily ?? null}
                compareCohortSize={compareUsers?.length ?? null}
                comparePills={comparePills}
              />
              <CompanyInsights
                companies={scopedCompanies}
                pagePills={pagePills}
                compareCompanies={scopedCompareCompanies}
                comparePills={comparePills}
              />
            </div>
          </>
        )}
      </div>

      <QueryInspector
        title="Onboarding baseline cohort"
        open={inspectorOpen}
        onClose={() => setInspectorOpen(false)}
        schemaContent={<OnboardingBaselineSchema />}
        explainContent={<OnboardingBaselineExplain />}
        queryResult={baselineResult.data ?? null}
        csvFilenamePrefix="onboarding-baseline-raw"
        sqlQueries={[
          { label: "Baseline cohort query", sql: BASELINE_SQL },
          { label: "All users query", sql: ALL_USERS_SQL },
          { label: "Demo'd companies query", sql: DEMO_SQL },
          { label: "Support role, no demo query", sql: SUPPORT_NO_DEMO_SQL },
          { label: "Support role, demo'd query", sql: SUPPORT_DEMO_SQL },
        ]}
      />
    </>
  );
}

